import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  FormGroup,
  Button,
  TextField,
  Avatar,
  Typography,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { updateUser } from '../../graphql/mutations';
import { Storage, API, Auth } from '../../services/amplifyCompat';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import PasswordRequirements from '../authentication/PasswordRequirements';
import { getAllGroupsOnlyUserOwns } from '../../graphql/queries'
import useTranslation from '../customHooks/translations';
import { prepareImageForUpload } from '../../utils/prepare-image-for-upload';
import {
  deleteAllGroupsWhereUserIsOnlyMember,
  reassignSubmittedActionsToGenericUser,
  deleteUser,
} from '../../graphql/mutations';

const StyledDialogTitle = styled(DialogTitle)`
  color: #0F172A;
  font-size: 2em;
  padding: .5em 1.5em 1em;
  font-weight: 600;
`;

const Input = styled('input')`
  display: none;
`;

const EditAccountInfo = ({
  open,
  databaseUser,
  setEditUser,
  editUser,
  getCurrentDatabaseUser,
}) => {
  const [avatarPreview, setAvatarPreview] = useState();
  const [avatarFile, setAvatarFile] = useState();
  const [avatarFileError, setAvatarFileError] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [currentPassIncorrectError, setCurrentPassIncorrectError] =
    useState(false);
  const [passwordsNotMatchError, setPasswordsNotMatchError] = useState(false);
  const [accountPasswordError, setAccountPasswordError] = useState(false);
  const [requiredAttributeError, setRequiredAttributeError] = useState(false);
  const [requiredPasswordError, setRequiredPasswordError] = useState(false);
  const initialUserForm = {
    name: databaseUser.name,
    email: databaseUser.email,
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };
  const translation = useTranslation();
  const [userInfoForm, setUserInfoForm] = useState(initialUserForm);
  const [passwordRequirements, setPasswordRequirements] = useState({
    uppercase: { error: false, description: 'oneUppercase' },
    lowercase: { error: false, description: 'oneLowercase' },
    digit: { error: false, description: 'oneDigit' },
    special: { error: false, description: 'oneSpecialCharacter' },
    minLength: {
      error: false,
      description: 'more8Characters',
    },
    maxLength: {
      error: false,
      description: 'less16Characters',
    },
  });
  const [infoUpdateSuccess, setInfoUpdateSuccess] = useState(false);
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showLeaveGroup, setShowLeaveGroup] = useState(false);
  const [groupsOwnedByUser, setGroupsOwnedByUser] = useState();
  const navigate = useNavigate();
  const [isAccountDeleting, setIsAccountDeleting] = useState(false);
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [infoSaveError, setInfoSaveError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [avatarRevision, setAvatarRevision] = useState(() => Date.now());

  const getAllUserOwnsGroups = useCallback(async () => {
    const groupRes = await API.graphql({
      query: getAllGroupsOnlyUserOwns,
      variables: { user_id: databaseUser.user_id },
    });
    const groups = groupRes.data.getAllGroupsOnlyUserOwns;
    setGroupsOwnedByUser(groups.filter(g => g.members_count > 1));
  }, [databaseUser.user_id]);

  useEffect(() => {
    databaseUser.user_id && getAllUserOwnsGroups()
  }, [databaseUser, getAllUserOwnsGroups])

  //closes dialog, resets states
  const handleClose = () => {
    setPasswordRequirements(() => {
      passwordRequirements.uppercase.error = false;
      passwordRequirements.lowercase.error = false;
      passwordRequirements.digit.error = false;
      passwordRequirements.special.error = false;
      passwordRequirements.minLength.error = false;
      passwordRequirements.maxLength.error = false;

      return passwordRequirements;
    });
    setEditUser(false);

    setCurrentPassIncorrectError(false);
    setAccountPasswordError(false);
    setPasswordsNotMatchError(false);
    setRequiredAttributeError(false);
    setRequiredPasswordError(false);
    setAvatarFileError(false);
    setUserInfoForm(initialUserForm);
    setInfoUpdateSuccess(false);
    setPasswordUpdateSuccess(false);
  };

  const checkMatchingPasswords = () => {
    if (
      !userInfoForm.currentPassword ||
      !userInfoForm.newPassword ||
      !userInfoForm.confirmNewPassword
    ) {
      throw new Error('empty');
    }
    if (userInfoForm.newPassword !== userInfoForm.confirmNewPassword) {
      throw new Error('Passwords do not match');
    }
  };

  const updateForm = (e) => {
    setUserInfoForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onChangePassword = (e) => {
    const { name, value } = e.target;

    // Only evaluate requirements against the new password field
    if (name === 'newPassword') {
      setPasswordRequirements((prev) => ({
        ...prev,
        uppercase: { ...prev.uppercase, error: /[A-Z]/.test(value) },
        lowercase: { ...prev.lowercase, error: /[a-z]/.test(value) },
        digit: { ...prev.digit, error: /[0-9]/.test(value) },
        special: { ...prev.special, error: /[^A-Za-z0-9]/.test(value) },
        minLength: { ...prev.minLength, error: value.length >= 8 },
        maxLength: { ...prev.maxLength, error: value.length <= 16 },
      }));
    }

    setUserInfoForm((prev) => {
      const next = { ...prev, [name]: value };
      // Re-check match whenever either password field changes
      if (name === 'newPassword' || name === 'confirmNewPassword') {
        const newPw = name === 'newPassword' ? value : next.newPassword;
        const confirmPw =
          name === 'confirmNewPassword' ? value : next.confirmNewPassword;
        if (confirmPw) {
          setPasswordsNotMatchError(newPw !== confirmPw);
        }
      }
      return next;
    });
  };

  //sends selected image to object storage
  async function handleAvatarChange(e) {
    if (!e.target.files || e.target.files.length === 0) {
      setAvatarPreview(null);
      return;
    }
    try {
      const imageFile = await prepareImageForUpload(e.target.files[0]);
      const previewLink = URL.createObjectURL(imageFile);
      setAvatarFile(imageFile);
      setAvatarPreview(previewLink);
      setAvatarFileError(false);
    } catch (error) {
      if (error?.code === 'UNSUPPORTED_IMAGE') {
        setAvatarFileError(true);
      } else {
        throw error;
      }
    }
  }

  const handleDeleteUser = async () => {
    const userId = databaseUser?.user_id;
    // Guard: require a valid user id; block if still sole owner of multi-member groups
    if (!userId) {
      setDeleteError(
        translation.deleteAccountError ||
          'Unable to delete account: missing user id.'
      );
      return false;
    }
    if (groupsOwnedByUser && groupsOwnedByUser.length > 0) {
      setShowDeleteWarning(false);
      setShowLeaveGroup(true);
      return false;
    }

    setIsAccountDeleting(true);
    setDeleteError(null);
    try {
      // 1. Delete all groups where user is the only member
      await API.graphql({
        query: deleteAllGroupsWhereUserIsOnlyMember,
        variables: { user_id: userId },
      });

      // 2. Reassign all user's submitted actions to generic user
      await API.graphql({
        query: reassignSubmittedActionsToGenericUser,
        variables: { user_id: userId },
      });

      // 3. Delete user from the app database
      await API.graphql({
        query: deleteUser,
        variables: { user_id: userId },
      });

      // 4. Delete user from Cognito
      await Auth.deleteUser();

      setShowDeleteWarning(false);
      setEditUser(false);
      navigate('/login', { replace: true });
      return true;
    } catch (error) {
      console.error('Error during account deletion:', error);
      setDeleteError(
        translation.deleteAccountError ||
          'Failed to delete account. Please try again.'
      );
      return false;
    } finally {
      setIsAccountDeleting(false);
    }
  };

  const handleDeleteAccountClick = () => {
    if (groupsOwnedByUser && groupsOwnedByUser.length > 0) {
      setShowLeaveGroup(true);
    }
    else {
      setShowDeleteWarning(true);
    }
  };

  const updateUserInfo = async () => {
    if (isSavingInfo) return;
    const { name } = userInfoForm;
    setIsSavingInfo(true);
    setInfoSaveError(null);
    setRequiredAttributeError(false);

    try {
      //update user in cognito
      //email is tied to the login and cannot be changed here, so it is never sent
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(cognitoUser, {
        name: name,
      });

      //handle updating user avatar
      let userAvatarLink;
      if (avatarFile) {
        const imageKey = 'avatars/' + databaseUser.user_id + 'avatar.png';
        const imageType = avatarFile.type;
        try {
          const uploadedAvatar = await Storage.put(imageKey, avatarFile, {
            contentType: imageType,
          });
          userAvatarLink = uploadedAvatar.url;
          setAvatarRevision((revision) => revision + 1);
        } catch (error) {
          console.log('Error uploading file', error);
          throw new Error('Avatar upload failed');
        }
      }
      //update user in db (email is intentionally left unchanged)
      await API.graphql({
        query: updateUser,
        variables: {
          user_id: databaseUser.user_id,
          name: name,
          email: databaseUser.email,
          avatar: userAvatarLink ? userAvatarLink : databaseUser.avatar,
        },
      });
      setInfoUpdateSuccess(true);
      getCurrentDatabaseUser(databaseUser.user_id);
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (e) {
      const errorMsg = e?.message || String(e);
      console.log(errorMsg);
      if (errorMsg.includes('Required attribute cannot be deleted')) {
        setRequiredAttributeError(true);
      } else {
        setInfoSaveError(
          errorMsg.includes('Avatar')
            ? translation.avatarUploadError || 'Avatar upload failed. Please try again.'
            : translation.profileSaveError || 'Failed to save profile. Please try again.'
        );
      }
    } finally {
      setIsSavingInfo(false);
    }
  };

  const updatePassword = async () => {
    if (isSavingPassword) return;
    const { currentPassword, newPassword } = userInfoForm;
    setIsSavingPassword(true);
    setCurrentPassIncorrectError(false);
    setPasswordsNotMatchError(false);
    setRequiredPasswordError(false);
    setAccountPasswordError(false);
    try {
      checkMatchingPasswords();
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(cognitoUser, currentPassword, newPassword);

      setPasswordUpdateSuccess(true);
      getCurrentDatabaseUser(databaseUser.user_id);
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (e) {
      const errorMsg = e?.message || String(e);
      if (errorMsg.includes('Incorrect username or password')) {
        setCurrentPassIncorrectError(true);
      } else if (errorMsg.includes('Passwords do not match')) {
        setPasswordsNotMatchError(true);
      } else if (errorMsg.includes('empty')) {
        setRequiredPasswordError(true);
      } else {
        setAccountPasswordError(true);
      }
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleMouseDownPassword = (e) => {
    e.preventDefault();
  };

  const renderSuccessMsg = () => {
    return (
      <>
        <StyledDialogTitle sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          {translation.success || 'Success!'}
        </StyledDialogTitle>
        <DialogContent sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          {infoUpdateSuccess
            ? translation.userInfoChanged || 'User information has been changed'
            : passwordUpdateSuccess &&
              (translation.passwordChanged || 'Password has been changed')}
        </DialogContent>
      </>
    );
  };

  const renderDeleteWarningDialog = () => {
    return (
      <Dialog
        aria-labelledby="delete-warning-dialog"
        PaperProps={{
          sx: {
            p: '1em',
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            alignItems: 'center',
          },
        }}
        open={showDeleteWarning}
        onClose={() => !isAccountDeleting && setShowDeleteWarning(false)}
      >
        <WarningAmberIcon fontSize="large" />
        <DialogContent>
          {isAccountDeleting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography>{translation.deleteAccountProgress}</Typography>
            </Box>
          ) : translation.deleteAccountWarning
          }
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', gap: '2em' }}>
          <Button
            variant="contained"
            onClick={() => setShowDeleteWarning(false)}
            disabled={isAccountDeleting}
          >
            {translation.cancel}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              handleDeleteUser().catch(() => {});
            }}
            disabled={isAccountDeleting}
          >
            {translation.delete}
          </Button>
        </DialogActions>
      </Dialog>
    )
  };

  const renderLeaveGroupsDialog = () => {
    return (
      <Dialog
        aria-labelledby="leave-groups-dialog"
        PaperProps={{
          sx: {
            p: '1em',
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            alignItems: 'center',
          },
        }}
        open={showLeaveGroup}
      >
        <IconButton
          sx={{ alignSelf: 'flex-end' }}
          onClick={() => setShowLeaveGroup(false)}
        >
          <CloseIcon />
        </IconButton>
        <WarningAmberIcon fontSize="large" />
        <DialogContent>{translation.deleteGroupsMsg}
          <Stack direction="column" alignItems="start" gap={1} sx={{marginTop: "1rem"}}>
            {groupsOwnedByUser?.map((group) => (
              <Button
                key={group.group_id}
                variant="outlined"
                color="error"
                sx={{
                  minWidth: "auto",
                  paddingInline: "0.5rem",
                  fontSize: "0.875rem",
                }}
                onClick={() => {
                  navigate(`/group-profile/${encodeURIComponent(group.group_name)}`);
                }}
              >
                {group.group_name}
              </Button>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    )
  };

  const renderEditInfoForm = () => {
    return (
      <>
        <IconButton sx={{ alignSelf: 'flex-end' }} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
        <StyledDialogTitle sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          {translation.editAccountInfoTitle}
        </StyledDialogTitle>
        <DialogContent
          sx={{
            p: '3em',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            overflow: 'initial',
          }}
        >
          <FormGroup
            sx={{
              mt: '2em',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1em',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1em',
              }}
            >
              {avatarFileError && (
                <Alert severity="error">
                  {translation.imageValidationError}
                </Alert>
              )}
              {databaseUser.avatar && !avatarPreview ? (
                <>
                  <Avatar
                    variant="rounded"
                    sx={{ height: 100, width: 100, alignSelf: 'center' }}
                    alt={`${databaseUser.name} avatar`}
                    src={
                      databaseUser.avatar
                        ? `${databaseUser.avatar}${databaseUser.avatar.includes('?') ? '&' : '?'}v=${avatarRevision}`
                        : undefined
                    }
                  />
                  <label htmlFor="action-icon-image">
                    <Input
                      accept="image/*"
                      id="action-icon-image"
                      type="file"
                      onChange={handleAvatarChange}
                    />
                    <Button variant="outlined" component="span">
                      {translation.uploadProfilePhoto}
                    </Button>
                  </label>
                </>
              ) : (
                <>
                  {avatarPreview ? (
                    <Avatar
                      variant="rounded"
                      sx={{
                        height: 100,
                        width: 100,
                      }}
                      alt="Uploaded Action Icon"
                      src={avatarPreview}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{
                        height: 100,
                        width: 100,
                      }}
                    />
                  )}

                  <label htmlFor="action-icon-image">
                    <Input
                      accept="image/*"
                      id="action-icon-image"
                      type="file"
                      onChange={handleAvatarChange}
                    />
                    <Button variant="outlined" component="span">
                      {translation.uploadProfilePhoto}
                    </Button>
                  </label>
                </>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1em',
                mt: '1em',
              }}
            >
              {requiredAttributeError && (
                <Alert severity="error">
                  {translation.requiredFieldsMessage}
                </Alert>
              )}
              <TextField
                required
                label={translation.name}
                name="name"
                value={userInfoForm.name}
                InputLabelProps={{ shrink: true }}
                onChange={updateForm}
              />

              <TextField
                disabled
                label={translation.email}
                name="email"
                value={userInfoForm.email}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                sx={{ xs: { mt: '1.5em' } }}
                helperText={
                  translation.emailTiedToLogin ||
                  'Your email is tied to your login and cannot be changed here.'
                }
              />

              {infoSaveError && (
                <Alert severity="error">{infoSaveError}</Alert>
              )}
              {requiredAttributeError && (
                <Alert severity="error">
                  {translation.requiredAttributeError ||
                    'A required attribute could not be updated.'}
                </Alert>
              )}

              <Button
                variant="contained"
                sx={{
                  mt: { sm: '0em', },
                }}
                onClick={updateUserInfo}
                disabled={isSavingInfo}
              >
                {isSavingInfo ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  translation.saveNameEmailPhoto
                )}
              </Button>
              <Button
                variant="outlined"
                color="error"
                sx={{
                  mt: { sm: '0em' },
                }}
                onClick={handleDeleteAccountClick}
              >
                {translation.deleteAccount}
              </Button>
            </Box>
          </FormGroup>
          <FormGroup
            sx={{
              mt: '4em',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1em',
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              variant="h7"
              sx={{ mb: '1em', fontWeight: 400 }}
            >
              {translation.changePassword}
            </Typography>
            {requiredPasswordError && (
              <Alert severity="error">
                {translation.requiredFieldsMessage}
              </Alert>
            )}
            <TextField
              required
              label={translation.currentPassword}
              name="currentPassword"
              value={userInfoForm.currentPassword}
              InputLabelProps={{ shrink: true }}
              type={showCurrentPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={translation.toggleCurrentPassword}
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showCurrentPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ xs: { mt: '1.5em' } }}
              onChange={updateForm}
              error={currentPassIncorrectError}
              helperText={
                currentPassIncorrectError && translation.currentPasswordMessage
              }
            />
            <TextField
              required
              label={translation.newPassword}
              name="newPassword"
              value={userInfoForm.newPassword}
              InputLabelProps={{ shrink: true }}
              type={showNewPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={translation.toggleNewPassword}
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showNewPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ xs: { mt: '1.5em' } }}
              onChange={onChangePassword}
              error={accountPasswordError}
              helperText={translation.passwordSettings}
            />
            <PasswordRequirements requirements={passwordRequirements} />

            <TextField
              required
              label={translation.confirmNewPassword}
              name="confirmNewPassword"
              value={userInfoForm.confirmNewPassword}
              InputLabelProps={{ shrink: true }}
              type={showConfirmNewPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={translation.toggleConfirmNewPassword}
                      onClick={() =>
                        setShowConfirmNewPassword(!showConfirmNewPassword)
                      }
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmNewPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ xs: { mt: '1.5em' } }}
              onChange={onChangePassword}
              error={passwordsNotMatchError}
              helperText={passwordsNotMatchError && translation.passwordsDontMatch}
            />
            <Button
              variant="contained"
              sx={{
                mt: { xs: '1.5em', sm: '0em' },
              }}
              onClick={updatePassword}
              disabled={isSavingPassword}
            >
              {isSavingPassword ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                translation.savePassword
              )}
            </Button>
          </FormGroup>
          {renderDeleteWarningDialog()}
          {renderLeaveGroupsDialog()}
        </DialogContent>
      </>
    );
  };

  return (
    <Dialog
      aria-labelledby="action-card-dialog"
      PaperProps={{ sx: { minWidth: '70%' } }}
      open={open}
      onClose={handleClose}
    >
      {/* render edit info form if password or info has not been successfully updated */}
      {infoUpdateSuccess || passwordUpdateSuccess
        ? renderSuccessMsg()
        : editUser && renderEditInfoForm()}
    </Dialog>
  );
};

export default EditAccountInfo;
